import { Component, forwardRef, Input } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * A date picker component that handles all date/time operations in Pacific Time (PT).
 *
 * Features:
 * - Converts input UTC dates to PT for display
 * - Handles ISO date strings with missing 'Z' suffix by treating them as UTC
 * - Returns all dates in ISO
 * - Implements ControlValueAccessor for form integration
 *
 * Usage:
 * ```html
 * <app-date-picker [(ngModel)]="date" placeholder="Select Date"></app-date-picker>
 * ```
 *
 * Input format: UTC date string (e.g. "2025-02-28T16:00:00" or "2025-02-28T16:00:00Z")
 * Output format: ISO date string (e.g. "2025-02-28T16:00:00.000Z")
 */

@Component({
  selector: 'app-date-picker',
  standalone: true,
  template:
    '<p-datepicker [inputId]="id" [class]="componentClass" [iconDisplay]="\'input\'" [showIcon]="showIcon" [placeholder]="placeholder" [disabled]="disabled" [(ngModel)]="date" (ngModelChange)="onChange($event)" (onBlur)="onTouched()" [appendTo]="appendTo"></p-datepicker>',
  imports: [FormsModule, DatePickerModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor {
  @Input() placeholder?: string = 'MM/DD/YYYY';
  @Input() class?: string = 'w-full';
  @Input() disabled: boolean | string = false;
  @Input() id?: string;
  @Input() appendTo?: string;
  @Input() invalid: boolean = false;

  date: Date | null = null;
  showIcon: boolean = true;

  get componentClass(): string {
    let baseClass = this.class || 'w-full';
    if (this.invalid) {
      baseClass += ' p-invalid';
    }
    return baseClass;
  }

  private toLocal(date: Date | string): Date {
    // If it's a string and doesn't have timezone info (Z or +/-), append Z to treat as UTC
    const hasTimezoneInfo = (date: string): boolean => {
      return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})/.test(date);
    };

    const dateStr = typeof date === 'string' && !hasTimezoneInfo(date) ? `${date}Z` : date;

    const ptDate = dayjs(dateStr).tz('America/Los_Angeles');

    return new Date(ptDate.format('YYYY-MM-DD HH:mm:ss'));
  }

  private asPtDate(date: Date): string {
    const ptDate = dayjs(dayjs(date).format('YYYY-MM-DD HH:mm:ss')).tz('America/Los_Angeles', true);
    return ptDate.toISOString();
  }

  writeValue(value: Date | string | null): void {
    if (value) {
      this.date = this.toLocal(value);
    } else {
      this.date = null;
    }
  }

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  onChange(value: Date | null): void {
    if (value) {
      this.onChangeFn(this.asPtDate(value));
    } else {
      this.onChangeFn(null);
    }
  }

  onTouched(): void {
    this.onTouchedFn();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.showIcon = !isDisabled;
    this.disabled = isDisabled;
  }
}
